import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { AppBox } from '../../../component/common/analog/molecule/AppBox'
import { useSkillAssessmentStore } from '../store/skill-assessment.store'
import { Fade, Tooltip, Typography, useTheme } from '@mui/material'
import { SkillSet } from '../model/skill-assessment-model'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Word } from 'src/feature/localization/component/atom/Word'
import { generateUniqId } from 'src/model/Id'
import StarsRoundedIcon from '@mui/icons-material/StarsRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import { SkillAssessmentLevels } from './SkillAssessmentLevels'

export type SkillAssessmentMenuProps = {
  initialOpenAssessment?: boolean
}

export const SkillAssessmentMenu = observer(({ initialOpenAssessment }: SkillAssessmentMenuProps) => {
  const skillAssessmentStore = useSkillAssessmentStore()
  const theme = useTheme()
  const { translation } = useLocalizationStore()
  const { isLoading } = skillAssessmentStore.getGenericFieldsState('getSkillSets')
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(initialOpenAssessment ?? false)

  useEffect(() => {
    skillAssessmentStore.reset()
    skillAssessmentStore.getSkillSets()
  }, [])

  const getSegmentColor = (userRating: number, expectedRating: number, index: number, isValidated: boolean) => {
    if (userRating && index <= userRating) {
      return isValidated ? { backgroundColor: theme.palette.primary.main } : { border: `2px solid ${theme.palette.primary.main}` }
    }
    if (expectedRating && index <= expectedRating) {
      return isValidated ? { backgroundColor: `${theme.palette.primary.main}33` } : { border: `2px solid ${theme.palette.primary.main}33` }
    }

    return { backgroundColor: theme.palette.background.dark }
  }

  const allSkills = skillAssessmentStore.skillSets?.flatMap(category => category.skills || []) || []
  const firstCategory = skillAssessmentStore.skillSets?.[0]
  const totalSkillsCount = allSkills.length

  return (
    <>
      <AppPaper sx={styles.root}>
        {skillAssessmentStore.skillSets && skillAssessmentStore.skillSets.length > 0 && (
          <AppBox alignItems="center" gap={5}>
            <Typography variant='h4'>Skills</Typography>
            <Word sx={styles.pill}>{totalSkillsCount}</Word>
          </AppBox>
        )}
        {!isLoading && (!skillAssessmentStore.skillSets || skillAssessmentStore.skillSets.length === 0) && (
          <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>{translation.noData()}</Caption>
        )}
        <AppBox
          flexDirection="column"
          gap={10}
          overflow="auto"
          width="100%"
        >
          {!isLoading && allSkills.map((skill: SkillSet) => (
            <AppBox key={skill.id} flexDirection="column" gap={10}>
              <AppBox gap={5} alignItems="center">
                <Typography sx={styles.ratingWrapper} fontSize={10}>
                  {skill.selectedSetLevel || 0}/{skill.expected_set_level || 0}
                </Typography>
                <Typography fontSize={12}>{skill.setName}</Typography>
              </AppBox>
              <AppBox gap={5} alignItems="center">
                {[...Array(5)].map((_, index) => (
                  <AppBox
                    key={generateUniqId()}
                    sx={{
                      ...styles.box,
                      ...getSegmentColor(skill.selectedSetLevel, skill.expected_set_level, index + 1, skill.isValidated),
                    }}
                  />
                ))}
                {(skill.selectedSetLevel !== null && skill.expected_set_level !== 0) && skill.isValidated && (
                  <Fade in timeout={800} mountOnEnter unmountOnExit>
                    <Tooltip title={translation.pages.myTeam.sortHeader.validated()}>
                      <VerifiedRoundedIcon fontSize='small' sx={{ color: Palette.GOLDEN_ORANGE }} />
                    </Tooltip>
                  </Fade>
                )}
                {(skill.selectedSetLevel !== null && skill.expected_set_level !== 0) && ((skill.selectedSetLevel ?? 0) > (skill.expected_set_level ?? 0)) && (
                  <Fade in timeout={800} mountOnEnter unmountOnExit>
                    <StarsRoundedIcon fontSize='small' sx={{ color: Palette.GOLDEN_ORANGE }} />
                  </Fade>
                )}

              </AppBox>
            </AppBox>
          ))}
          {isLoading && (
            <AppSkeleton
              count={5}
              direction="column"
              rects={[
                { width: '20%', height: 15 },
                { width: '90%', height: 15 },
                { width: '90%', height: 15 },
              ]}
            />
          )}
        </AppBox>
      </AppPaper>
      {isAssessmentModalOpen && firstCategory && (
        <SkillAssessmentLevels
          skillAssessmentSetIndex={0}
          isModalOpen={isAssessmentModalOpen}
          isModalClose={() => setIsAssessmentModalOpen(false)}
          categoryId={firstCategory.id}
        />
      )}
    </>
  )
})

const styles = {
  root: {
    padding: '10px 15px',
    borderRadius: '12px',
    gap: 10,
    width: '100%',
    border: 0,
  },
  ratingWrapper: {
    backgroundColor: 'background.dark',
    color: 'text.primary',
    border: '1px solid',
    padding: '3px 6px',
    borderRadius: '10px',
  },
  box: {
    height: 15,
    width: '10%',
    borderRadius: '10px',
  },
  pill: {
    width: 25,
    height: 25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '999px',
    backgroundColor: ColorTokens.primary10,
    color: ColorTokens.primary500,
  },
}
